import { useMemo } from 'react';

/**
 * Allowed HTML tags for sanitization
 * Only these tags will be preserved; all others are stripped
 */
const ALLOWED_TAGS = [
    'span', 'strong', 'b', 'em', 'i', 'u', 's', 'del', 'ins',
    'sup', 'sub', 'small', 'br', 'wbr'
];

/**
 * Allowed attributes for sanitization
 * Only these attributes will be preserved on allowed tags
 */
const ALLOWED_ATTRIBUTES = [ 'class', 'id' ];

/**
 * Dangerous attribute prefixes to remove
 */
const DANGEROUS_ATTR_PREFIXES = [ 'on', 'javascript:', 'data-' ];

/**
 * Sanitize HTML string by removing dangerous content
 *
 * Uses DOMParser for safe parsing and only preserves allowed tags/attributes.
 * This prevents XSS attacks while allowing basic formatting.
 *
 * @param {string} html - Raw HTML string to sanitize
 * @return {string} Sanitized HTML string
 */
export const sanitizeHtml = ( html ) => {
    if ( !html || typeof html !== 'string' ) {
        return '';
    }

    // Use DOMParser to safely parse HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString( html, 'text/html' );

    /**
     * Recursively sanitize a DOM node
     *
     * @param {Node} node - DOM node to sanitize
     * @return {string} Sanitized HTML string
     */
    const sanitizeNode = ( node ) => {
        // Text nodes - return escaped content
        if ( node.nodeType === Node.TEXT_NODE ) {
            return escapeHtml( node.textContent || '' );
        }

        // Element nodes - check if allowed
        if ( node.nodeType === Node.ELEMENT_NODE ) {
            const tagName = node.tagName.toLowerCase();

            // If tag is not allowed, just return the text content of children
            if ( !ALLOWED_TAGS.includes( tagName ) ) {
                return Array.from( node.childNodes )
                    .map( sanitizeNode )
                    .join( '' );
            }

            // Build sanitized element
            let attributes = '';
            for ( const attr of node.attributes ) {
                const attrName = attr.name.toLowerCase();

                // Skip dangerous attributes
                const isDangerous = DANGEROUS_ATTR_PREFIXES.some(
                    prefix => attrName.startsWith( prefix )
                );
                if ( isDangerous ) {
                    continue;
                }

                // Only keep allowed attributes
                if ( ALLOWED_ATTRIBUTES.includes( attrName ) ) {
                    // Escape attribute value
                    const safeValue = escapeHtml( attr.value );
                    attributes += ` ${ attrName }="${ safeValue }"`;
                }
            }

            // Self-closing tags
            if ( tagName === 'br' || tagName === 'wbr' ) {
                return `<${ tagName }${ attributes } />`;
            }

            // Regular tags with children
            const children = Array.from( node.childNodes )
                .map( sanitizeNode )
                .join( '' );

            return `<${ tagName }${ attributes }>${ children }</${ tagName }>`;
        }

        return '';
    };

    // Sanitize the body content
    return Array.from( doc.body.childNodes )
        .map( sanitizeNode )
        .join( '' );
};

/**
 * Escape HTML special characters
 *
 * @param {string} text - Text to escape
 * @return {string} Escaped text
 */
const escapeHtml = ( text ) => {
    const div = document.createElement( 'div' );
    div.textContent = text;
    return div.innerHTML;
};

/**
 * SafeHtml Component
 *
 * Renders sanitized HTML content safely.
 * Use this instead of dangerouslySetInnerHTML for user-provided or external HTML.
 *
 * @param {Object} props
 * @param {string} props.html - HTML string to sanitize and render
 * @param {string} props.className - Optional CSS class name
 * @param {string} props.as - Element type to render (default: 'span')
 * @param {string} props.fallback - Fallback text if html is empty
 */
const SafeHtml = ( { html, className, as: Component = 'span', fallback = '' } ) => {
    const sanitizedHtml = useMemo( () => {
        if ( !html ) {
            return fallback;
        }
        return sanitizeHtml( html );
    }, [ html, fallback ] );

    // If no HTML content, render fallback as text
    if ( !html && fallback ) {
        return <Component className={ className }>{ fallback }</Component>;
    }

    // Render sanitized HTML
    return (
        <Component
            className={ className }
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />
    );
};

export default SafeHtml;
